
import { createFileRoute, Link } from "@tanstack/react-router";
import { MessagesSquare, FileText, Handshake, ArrowUpRight } from "lucide-react";
import { CaseSpotlight } from "@/components/verbandskommunikation/CaseSpotlight";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { RotatingWord } from "@/components/ui/rotating-word";
import { MonoLogoBar } from "@/components/logos/MonoLogoBar";
import { SpeechBubbleAnimation } from "@/components/politisch-kommunizieren/SpeechBubbleAnimation";
import { ConfettiOverlay } from "@/components/verbandskommunikation/ConfettiOverlay";
import { lazySection } from "@/lib/lazy-section";
import { casesByTagQueryOptions, clientLogosQueryOptions } from "@/lib/public-content.queries";
import direktmandatAsset from "@/assets/direktmandat-anruf.webp.asset.json";
import ausschliesslichPolitischAsset from "@/assets/ausschliesslich-politisch.webp.asset.json";
import politischesSprachgefuehlAsset from "@/assets/politisches-sprachgefuehl.webp.asset.json";
import mockupOedpAsset from "@/assets/mockup-oedp.webp.asset.json";
import cduGuidelinesAsset from "@/assets/cases/cdu-digital-guidelines.webp.asset.json";
import strategieUmsetzungAsset from "@/assets/strategie-umsetzung.webp.asset.json";
import verlaesslichkeitAsset from "@/assets/verlaesslichkeit-alltag.webp.asset.json";
import socialMediaAsset from "@/assets/social-media-v2.webp.asset.json";
import presseMedienarbeitAsset from "@/assets/presse-medienarbeit.webp.asset.json";
import mitgliederkommunikationAsset from "@/assets/creategy-newsletter.svg.asset.json";
import kampagneMobilisierungAsset from "@/assets/creategy-mitglieder.svg.asset.json";
import teamRioAsset from "@/assets/parteikampagnen/team-rio.png.asset.json";
import analyseAsset from "@/assets/analyse-strategie.webp.asset.json";
import publikationenChronikenAsset from "@/assets/chroniken.svg.asset.json";
import executiveBrandingAsset from "@/assets/corporate-design.svg.asset.json";
import { BookingCtaButton, INQUIRY_CTA_LABEL } from "@/components/cta/BookingCtaButton";


const CasesSection = lazySection(async () => {
  const m = await import("@/components/cases/CasesSection");
  return { default: m.CasesSection };
}, { minHeight: 480 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
});


export const Route = createFileRoute("/politisch-kommunizieren/politische-kommunikation")({
  loader: async ({ context }) => {
    // Cases stecken in einer lazy geladenen Sektion → nur prefetchen, nicht awaiten.
    void context.queryClient.prefetchQuery(casesByTagQueryOptions("politische-kommunikation"));
    // MonoLogoBar rendert serverseitig mit useSuspenseQuery → muss bereitstehen.
    return Promise.all([context.queryClient.ensureQueryData(clientLogosQueryOptions)]);
  },

  head: () => ({
    meta: [
      { title: "Politische Kommunikation | creategy" },
      {
        name: "description",
        content:
          "Laufende politische Kommunikation für Parteien und Fraktionen. Strategisch, kreativ und zuverlässig. Damit Ihre Themen gehört werden, auch zwischen den Wahlen.",
      },
      { property: "og:title", content: "Politische Kommunikation | creategy" },
      {
        property: "og:description",
        content:
          "Laufende politische Kommunikation für Parteien und Fraktionen. Strategisch, kreativ und zuverlässig. Damit Ihre Themen gehört werden, auch zwischen den Wahlen.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://creategy.de/politisch-kommunizieren/politische-kommunikation" },
      { property: "og:site_name", content: "creategy" },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Politische Kommunikation | creategy" },
      {
        name: "twitter:description",
        content:
          "Laufende politische Kommunikation für Parteien und Fraktionen. Strategisch, kreativ und zuverlässig. Damit Ihre Themen gehört werden, auch zwischen den Wahlen.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://creategy.de/politisch-kommunizieren/politische-kommunikation" },
    ],
  }),
  component: PolitischeKommunikationPage,
});

function PolitischeKommunikationPage() {
  const gruende = [
    {
      title: "Ausschließlich politisch.",
      text: "Kein Kunde aus der Waschmittelbranche, kein Imagefilm für Zahnpasta. Wir arbeiten nur politisch und kennen die Logik von Parteien, Fraktionen und Gremien aus eigener Erfahrung.",
      image: { src: ausschliesslichPolitischAsset.url, alt: "Strategie-Notizen auf Whiteboard" },
    },
    {
      title: "Verlässlichkeit im Alltag.",
      text: "Politische Kommunikation duldet keine Pausen. Wir liefern kontinuierlich: mit festen Abläufen, eingespielter Redaktionsplanung und einem Team, das auch dann arbeitet, wenn bei Ihnen Sitzungswoche ist.",
      image: { src: verlaesslichkeitAsset.url, alt: "Mitarbeiter konzentriert am Laptop" },
    },
    {
      title: "Politisches Sprachgefühl.",
      text: "Wir wissen, was eine Fraktion sagen kann und was ein Landesverband sagen sollte. Unsere Texte bestehen den Test vor Gremien, Mitgliedern und Medien.",
      image: { src: politischesSprachgefuehlAsset.url, alt: "Beratungsgespräch am Tisch" },
    },
    {
      title: "Strategie und Umsetzung aus einer Hand.",
      text: "Wir denken Ihre Kommunikation strategisch und setzen sie selbst um. Kein Reibungsverlust zwischen Beratung und Produktion, keine Übersetzungsfehler zwischen Konzept und Post.",
      image: { src: strategieUmsetzungAsset.url, alt: "Strategiepräsentation auf Bildschirm" },
    },
    {
      title: "Ihr Direktmandat zu uns.",
      text: 'Kein Ticketsystem, keine Warteschleife, kein „Ich leite das weiter." Bei creategy haben Sie einen festen Ansprechpartner: erreichbar per Telefon, Mail und WhatsApp.',
      image: { src: direktmandatAsset.url, alt: "Ansprechpartner am Telefon" },
    },

  ];

  const gruendeImages = gruende.map((card) => ({ src: card.image.src, alt: card.image.alt }));

  return (
    <Layout>
      {/* Hero */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div className="relative">
              <h1
                id="hero"
                lang="de"
                className="heading-h1 relative z-10 text-brand-kommunikation hyphens-auto-de md:[hyphens:none] md:w-[145%]"
              >
                Wir machen aus
                <br />
                <RotatingWord
                  words={[
                    "Anträgen Botschaften.",
                    "Positionen Schlagzeilen.",
                    "Sitzungen Geschichten.",
                    "Mitgliedern Mitstreiter.",
                  ]}
                />
              </h1>

              <p
                className="fade-in-up mt-10 font-body text-base leading-relaxed text-foreground md:text-lg"
                style={{ animationDelay: "160ms" }}
              >
                Wahlkampf ist ein Sprint, politische Relevanz ein Marathon. Wir halten Ihre Partei, Fraktion oder Organisation dauerhaft sichtbar: strategisch geplant, professionell umgesetzt, Tag für Tag.
              </p>
              <div
                className="fade-in-up mt-10 flex flex-col items-stretch gap-3 lg:flex-row lg:items-center"
                style={{ animationDelay: "240ms" }}
              >
                <Link
                  to="/kontakt"
                  search={{ anliegen: "politische-kommunikation" }}
                  className="inline-flex w-full items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 lg:w-auto"
                >
                  {INQUIRY_CTA_LABEL}
                </Link>
                <BookingCtaButton variant="secondary" eventPath="creategy/erstgespraech" />
              </div>
            </div>

            <div className="order-first md:order-none flex w-full items-center justify-center">
              <SpeechBubbleAnimation />
            </div>
          </div>
        </div>
      </section>

      <MonoLogoBar
        fixedCategory="politische-kommunikation"
        ariaLabel="Ausgewählte Kunden aus der politischen Kommunikation"
        minCount={3}
        bordered={false}
        gapClassName="gap-6 sm:gap-16"
      />

      {/* Zwischen den Wahlen wird gewonnen */}
      <section
        aria-labelledby="zwischen-wahlen"
        className="relative overflow-hidden"
      >
        <ConfettiOverlay />
        <div className="section-y relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div>
            <Reveal>
              <h2
                id="zwischen-wahlen"
                className="heading-h2 text-brand-kommunikation"
              >
                Zwischen den Wahlen wird gewonnen.
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <div className="heading-lead">
                <p className="font-body text-base leading-relaxed text-foreground md:text-lg">
                  Am Wahlabend zeigt sich nur, was in den Jahren davor aufgebaut wurde: Bekanntheit, Vertrauen, Themenhoheit. Doch genau diese Daueraufgabe bleibt im politischen Alltag oft liegen. Geschäftsstellen und Fraktionsbüros sind mit Anträgen, Gremien und Tagesgeschäft ausgelastet, und die eigene Kommunikation läuft nebenher, wenn Zeit bleibt.
                </p>
                <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                  Wir schließen diese Lücke. creategy übernimmt Ihre laufende Kommunikation oder einzelne Projekte, mit einem Team, das ausschließlich politisch arbeitet: aus über zweihundert Wahlkämpfen, aus der täglichen Betreuung von Parteien und aus Projekten für Fraktionen. Sie gewinnen Kapazität, Professionalität und einen Partner, der Ihre Sprache spricht.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>



      {/* Bausteine */}
      <section aria-labelledby="bausteine">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="bausteine"
              className="heading-h2 text-brand-kommunikation"
            >
              Unsere Leistungen für Ihre politische Kommunikation.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Ob dauerhafte Betreuung oder einzelnes Projekt: Sie buchen bei uns genau die Unterstützung, die Ihre Organisation braucht. Alle Bausteine kommen aus einer Hand und greifen ineinander.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
            {[
              { title: "Social-Media-Betreuung", text: "Wir übernehmen auf Wunsch Ihre Kanäle komplett: Redaktionsplanung, Content-Produktion, Community Management und Auswertung. Sie sind dauerhaft präsent, ohne dass intern jemand daran zerbricht.", image: socialMediaAsset.url, imageAlt: "Like- und Reaktions-Icons als Symbol für Social-Media-Content", fullBleed: true, imageClassName: "object-cover object-bottom md:h-[14rem] md:object-cover md:object-bottom lg:h-[16rem] lg:object-cover" },
              { title: "Pressearbeit", text: "Von der Pressemitteilung bis zum Hintergrundgespräch: Wir bringen Ihre Positionen in die Medien und sorgen dafür, dass Ihre Stimme im politischen Diskurs gehört wird, lokal wie überregional.", image: presseMedienarbeitAsset.url, imageAlt: "Stapel lokaler Zeitungen als Symbol für Presse- und Medienarbeit", imageClassName: "md:h-[12rem] lg:h-[12rem] lg:translate-y-0" },
              { title: "Mitgliederkontakt & Newsletter", text: "Ihre Mitglieder sind Ihr größtes Kapital. Wir entwickeln interne Formate, die aktivieren und die Bindung an Ihre Organisation stärken.", image: mitgliederkommunikationAsset.url, imageAlt: "Bunte Sprechblasen als Symbol für Mitgliederkommunikation" },
              { title: "Mitgliederwerbung", text: "Wachstum passiert nicht von allein. Wir konzipieren Kampagnen zur Mitgliedergewinnung.", image: kampagneMobilisierungAsset.url, imageAlt: "Netzwerk vernetzter Personen-Icons als Symbol für Mitgliederwerbung", fullBleed: true },
              { title: "Websites & digitale Infrastruktur", text: "Ihre Website ist Ihre Geschäftsstelle im Netz. Wir konzipieren, gestalten und betreuen digitale Auftritte für Parteien, Fraktionen und Abgeordnete.", image: teamRioAsset.url, imageAlt: "Rio (creategy) am Laptop – Symbol für operative Website-Beratung" },
              { title: "Themenkampagnen", text: "Manche Anliegen brauchen mehr als einen Post. Wir entwickeln Mikrokampagnen zu einzelnen politischen Themen: mit klarer Botschaft und passenden Formaten.", image: analyseAsset.url, imageAlt: "Analyse und Strategie als Symbol für zielgruppengenaue Themenkampagnen", imageClassName: "md:pb-6 lg:pb-0" },
              { title: "Publikationen & Chroniken", text: "Zum Jubiläum oder als Standortbestimmung: Wir konzipieren und gestalten Publikationen, die die Arbeit Ihrer Organisation sichtbar und bleibend dokumentieren.", image: publikationenChronikenAsset.url, imageAlt: "Hände halten Zeitungen und ein Mikrofon als Symbol für Publikationen und Chroniken", fullBleed: true, imageClassName: "pb-6 md:pb-8" },
              { title: "Corporate Design", text: "Ein einheitliches Erscheinungsbild schafft Wiedererkennung. Wir entwickeln Ihr Design und bringen alle Materialien auf eine Linie, vom Logo über Vorlagen bis zum Social-Media-Auftritt.", image: executiveBrandingAsset.url, imageAlt: "Illustration als Symbol für Corporate Design", fullBleed: true },
            ].map((b, i) => {
              const span = i < 2 ? "lg:col-span-3" : "lg:col-span-2";
              const hasImage = "image" in b && b.image;
              const isFullBleed = "fullBleed" in b && b.fullBleed;
              return (
                <Reveal key={b.title} delay={i * 60} className={`${span} h-full`}>
                  <article className="flex h-[20rem] flex-col overflow-hidden rounded-md bg-brand-kommunikation p-6 text-background md:h-[24rem] md:p-8">
                    <h3 className="heading-h3 text-background">
                      {b.title}
                    </h3>
                    <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                      {b.text}
                    </p>
                    {hasImage ? (
                      isFullBleed ? (
                        <div className="-mx-6 -mb-6 mt-auto flex items-end justify-center md:-mx-8 md:-mb-8">
                          <img
                            src={b.image as string}
                            alt={b.imageAlt ?? ""}
                            className={`block h-[9.5rem] w-full object-contain object-bottom md:h-[12rem] ${(b as any).imageClassName ?? ""}`}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>

                      ) : (
                        <div className={`-mx-6 -mb-8 mt-auto flex h-[9.5rem] shrink-0 items-end justify-center px-4 md:-mx-8 md:-mb-10 md:h-[12rem] md:px-6 ${(b as any).imageClassName ?? ""}`}>
                          <img
                            src={b.image as string}
                            alt={b.imageAlt ?? ""}
                            className="block h-full w-full object-contain object-bottom"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      )
                    ) : (
                      <div
                        aria-hidden="true"
                        className="mt-auto flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-border bg-secondary/40"
                      >
                        <span className="font-display text-xs uppercase tracking-wider text-muted-foreground">
                          Bild folgt
                        </span>
                      </div>
                    )}
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>


      {/* Spotlights */}
      <section aria-labelledby="spotlights">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="spotlights"
              className="heading-h2 text-brand-kommunikation"
            >
              Zwei Projekte, die zeigen, wie wir arbeiten.
            </h2>
          </Reveal>
          <div className="mt-16 space-y-24 md:space-y-32">
            <CaseSpotlight
              
              title="Ein digitales Corporate Design für die CDU Deutschlands"
              text="Ein etabliertes Erscheinungsbild, neu gedacht für die digitale Welt: Für die CDU Deutschlands haben wir das bestehende Corporate Design für Social Media weiterentwickelt und mobil optimiert. Mit einem neuen Schriftmix, überarbeiteten Logovarianten und Gestaltungsprinzipien, die auf kleinen Bildschirmen funktionieren. Damit die Marke CDU auch im Feed unverwechselbar bleibt."
              mediaLeft
              ctaTo="/cases/cdu-deutschlands"
              ctaLabel="Projekt entdecken"
            >
              <div className="aspect-[3/2] overflow-hidden rounded-md bg-secondary/40">
                <img
                  src={cduGuidelinesAsset.url}
                  alt="Styleguide Online Design der CDU Deutschlands – Farben, Typografie und Anwendungsbeispiele"
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </CaseSpotlight>
            <CaseSpotlight
              
              title="Dauerhaft sichtbar: Social Media für die ÖDP."
              text="Seit über einem Jahr verantworten wir die Social-Media-Kommunikation der ÖDP: von der Redaktionsplanung über die Produktion bis zum Community Management. Eine Partei, die dauerhaft präsent ist, ohne dass die eigene Geschäftsstelle daran hängt. Genau so funktioniert ein Retainer bei uns."
              mediaLeft={false}
              ctaTo="/cases/oedp"
              ctaLabel="Projekt entdecken"
            >
              <div className="aspect-[3/2] overflow-hidden rounded-md bg-secondary/40">
                <img
                  src={mockupOedpAsset.url}
                  alt="Social-Media-Mockup der ÖDP – Instagram-Feed und Kachel-Serien"
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </CaseSpotlight>
          </div>

        </div>
      </section>


      {/* Cases & Referenzen */}
      <CasesSection
        tag="politische-kommunikation"
        title="Weitere erfolgreiche Projekte."
        description="Einblicke in unsere Arbeit für Parteien, Fraktionen und politische Organisationen."
      />


      {/* Übergang zu Wahlkampfleistungen */}
      <section aria-labelledby="wahlkampfleistungen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="wahlkampfleistungen" className="heading-h2 text-brand-kommunikation">
              Und wenn die nächste Wahl ansteht?
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-6 font-body text-base leading-relaxed text-foreground">
              Dann sind wir längst eingearbeitet. Aus laufender Kommunikation wird bei uns nahtlos Wahlkampf: mit denselben Ansprechpartnern, derselben Strategie und der Erfahrung aus über zweihundert Kampagnen. Alles zu unseren Wahlkampfleistungen finden Sie hier.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <Link
              to="/wahlen-gewinnen/parteikampagnen"
              className="story-link mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-kommunikation"
            >
              Zu unseren Wahlkampfleistungen
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 5 Gründe für creategy – Sticky-Stack der Karten, Bild links parallel sticky */}
      <section
        aria-labelledby="gruende"
        className="relative isolate z-0 bg-background text-foreground"
      >
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 md:mb-10">
            <Reveal>
              <h2 id="gruende" className="heading-h2 text-brand-kommunikation">
                5 Gründe für creategy
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="mt-4 font-body text-sm leading-relaxed text-foreground/85 md:text-base">
                Warum Parteien, Fraktionen und politische Organisationen sich für creategy entscheiden.
              </p>
            </Reveal>
          </div>

          <ol className="flex flex-col gap-4 md:gap-0">
            {gruende.map((card, i) => {
              const img = gruendeImages[i];
              return (
                <li
                  key={card.title}
                  className="md:sticky md:top-32 md:pb-[6.66vh]"
                  style={{ zIndex: 10 + i }}
                >
                  <article className="grid gap-8 rounded border border-brand-kommunikation/20 bg-background p-7 shadow-[0_10px_30px_-12px_rgba(0,131,153,0.18)] md:min-h-[28rem] md:grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)] md:items-stretch md:gap-8 md:p-9 lg:min-h-0">
                    <div className="relative aspect-square w-full max-w-[360px] overflow-hidden rounded border border-brand-kommunikation/20 bg-brand-kommunikation/5">
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex h-full flex-col justify-center">
                      <div>
                        <h3 className="heading-h3 text-brand-kommunikation">
                          {card.title}
                        </h3>
                        <p className="mt-4 font-body text-base leading-relaxed text-foreground/85">
                          {card.text}
                        </p>
                      </div>
                      <div className="mt-6 font-display text-sm font-extrabold tracking-wider text-brand-kommunikation/60">
                        Grund {i + 1} von {gruende.length}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      </section>


      {/* Prozess */}
      <section aria-labelledby="prozess-pk">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="prozess-pk" className="heading-h2 text-brand-kommunikation">
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline
              headingColor="text-brand-kommunikation"
              steps={[
                {
                  title: "Kostenloses Erstgespräch",
                  text: "Wir lernen Ihre Organisation kennen: Ihre Ziele, Ihre Strukturen, Ihre Kanäle. Sie erfahren, wie professionelle politische Kommunikation für Sie aussehen kann. Digital oder bei Ihnen vor Ort.",
                  Icon: MessagesSquare,
                },
                {
                  title: "Konzept & Angebot",
                  text: "Sie erhalten ein konkretes Konzept mit klarem Leistungsumfang: als laufender Retainer, als Projekt oder als Kombination aus beidem. Transparent kalkuliert und auf Ihr Budget zugeschnitten.",
                  Icon: FileText,
                },
                {
                  title: "Umsetzung",
                  text: "Wir legen los. Sie behalten die politische Kontrolle, wir übernehmen die kommunikative Arbeit: zuverlässig, abgestimmt und in Ihrer Sprache.",
                  Icon: Handshake,
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Abschluss-CTA */}
      <section className="w-full bg-brand-kommunikation section-y text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-[1fr_auto] gap-10 lg:items-center text-left">
          <div>
            <Reveal>
              <h2 className="text-white text-3xl md:text-5xl leading-tight mb-8">
                Sichtbarkeit ist kein Zufall.
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="text-white/90 text-lg leading-relaxed mb-5">
                Ob dauerhafte Betreuung oder einzelnes Projekt: In einem ersten
                Gespräch schauen wir gemeinsam auf Ihre Kommunikation und zeigen
                Ihnen, welche Möglichkeiten in Ihrer Organisation stecken.
                Kostenlos, unverbindlich und vertraulich.
              </p>
              <p className="text-white/90 text-lg leading-relaxed">
                Je früher Sie Ihre Kommunikation professionalisieren, desto
                stärker stehen Sie da, wenn es darauf ankommt. Die nächste Wahl
                kommt bestimmt.
              </p>
            </Reveal>
          </div>
          <Reveal delay={160}>
            <div className="flex flex-col items-stretch gap-3 self-start lg:justify-self-end">
              <Link
                to="/kontakt"
                search={{ anliegen: "politische-kommunikation" }}
                className="inline-flex items-center justify-center rounded-md bg-brand-button px-8 py-4 font-display text-base font-extrabold text-brand-button-foreground transition-all duration-200 hover:brightness-95"
              >
                {INQUIRY_CTA_LABEL}
              </Link>
              <BookingCtaButton variant="secondaryOnDark" eventPath="creategy/erstgespraech" className="h-14 text-base" />
            </div>
          </Reveal>
        </div>
      </section>

    </Layout>
  );
}
